import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import type { TextFieldProps } from '@mui/material/TextField';

type TextFieldSlotProps = NonNullable<TextFieldProps['slotProps']>;

export type AutocompleteTextFieldSlotOverrides = {
  input?: TextFieldSlotProps['input'];
  inputLabel?: TextFieldSlotProps['inputLabel'];
  htmlInput?: TextFieldSlotProps['htmlInput'];
};

export type AutocompleteTextFieldParams = Omit<AutocompleteRenderInputParams, 'slotProps'>;

/**
 * Omits Autocomplete `renderInput` slot props before spreading onto `TextField`.
 */
export function omitAutocompleteTextFieldParams(
  params: AutocompleteRenderInputParams,
): AutocompleteTextFieldParams {
  const { slotProps: _slotProps, ...rest } = params;
  return rest;
}

/**
 * Maps Autocomplete `renderInput` params to `TextField` `slotProps`.
 */
export function autocompleteTextFieldSlotProps(
  params: AutocompleteRenderInputParams,
  overrides?: AutocompleteTextFieldSlotOverrides,
): TextFieldSlotProps {
  const { slotProps } = params;
  const result: TextFieldSlotProps = {
    input: overrides?.input ? { ...slotProps.input, ...overrides.input } : slotProps.input,
    inputLabel: overrides?.inputLabel
      ? { ...(slotProps.inputLabel as object), ...overrides.inputLabel }
      : slotProps.inputLabel,
    htmlInput: overrides?.htmlInput
      ? { ...slotProps.htmlInput, ...overrides.htmlInput }
      : slotProps.htmlInput,
  };
  return result;
}

/**
 * Merges base Autocomplete slot props with optional overrides (e.g. `renderedInputProps`).
 */
export function mergeAutocompleteTextFieldSlotProps(
  base: TextFieldSlotProps,
  extra?: AutocompleteTextFieldSlotOverrides,
): TextFieldSlotProps {
  if (!extra) {
    return base;
  }
  const merged: TextFieldSlotProps = { ...base };
  if (extra.input) {
    merged.input = { ...(base.input as object), ...extra.input } as TextFieldSlotProps['input'];
  }
  if (extra.inputLabel) {
    merged.inputLabel = {
      ...(base.inputLabel as object),
      ...extra.inputLabel,
    } as TextFieldSlotProps['inputLabel'];
  }
  if (extra.htmlInput) {
    merged.htmlInput = {
      ...(base.htmlInput as object),
      ...extra.htmlInput,
    } as TextFieldSlotProps['htmlInput'];
  }
  return merged;
}
